import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TimelineItemWithCommentCount } from "@travel/shared";
import { api } from "../api/client";
import { usePlan } from "../hooks/usePlan";
import { useNow } from "../hooks/useNow";
import { Timeline } from "../components/timeline/Timeline";
import { ItemActionSheet } from "../components/sheets/ItemActionSheet";
import { ItemEditDialog } from "../components/sheets/ItemEditDialog";
import { JsonItemsDialog } from "../components/sheets/JsonItemsDialog";
import { CommentDrawer } from "../components/sheets/CommentDrawer";
import { MembersDialog } from "../components/sheets/MembersDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";

type EditTarget = TimelineItemWithCommentCount | "new" | null;

export function PlanDetailPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: plan, isLoading, error } = usePlan(planId);
  const now = useNow();

  const [actionItem, setActionItem] = useState<TimelineItemWithCommentCount | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [commentItem, setCommentItem] = useState<TimelineItemWithCommentCount | null>(null);
  const [deletePlanOpen, setDeletePlanOpen] = useState(false);
  const [jsonItemsOpen, setJsonItemsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  const deletePlanMutation = useMutation({
    mutationFn: () => api.deletePlan(planId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plans"] });
      navigate("/");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-500">
        불러오는 중…
      </div>
    );
  }
  if (error || !plan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-2">
        <p className="text-rose-600">불러오기 실패</p>
        <Link to="/" className="text-slate-500 underline">
          목록으로
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-24">
      <header
        className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200 px-3 py-2.5 flex items-center gap-2"
        style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}
      >
        <Link
          to="/"
          className="px-2 py-1 text-slate-700 hover:bg-slate-100 rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center"
          aria-label="목록으로"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="flex-1 font-semibold text-slate-900 truncate">{plan.title}</h1>
        <button
          type="button"
          onClick={() => setMembersOpen(true)}
          className="px-2 py-1 text-slate-700 hover:bg-slate-100 rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center"
          aria-label="멤버 관리"
          title="멤버 관리"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => setJsonItemsOpen(true)}
          className="px-2 py-1 text-slate-700 hover:bg-slate-100 rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center"
          aria-label="JSON으로 일정 추가/수정"
          title="JSON으로 일정 추가/수정"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2" />
            <path d="M16 3h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => setEditTarget("new")}
          className="px-2 py-1 text-slate-700 hover:bg-slate-100 rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center"
          aria-label="일정 추가"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </button>
        {plan.role === "owner" && (
          <button
            type="button"
            onClick={() => setDeletePlanOpen(true)}
            className="px-2 py-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600 rounded-lg min-h-[36px] min-w-[36px] flex items-center justify-center"
            aria-label="계획 삭제"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        )}
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4">
        <Timeline
          planId={plan.id}
          items={plan.items}
          now={now}
          onItemClick={(it) => setActionItem(it)}
          onCommentIconClick={(it) => setCommentItem(it)}
        />
      </main>

      <ItemActionSheet
        item={actionItem}
        planId={plan.id}
        onClose={() => setActionItem(null)}
        onEdit={(it) => {
          setActionItem(null);
          setEditTarget(it);
        }}
        onComment={(it) => {
          setActionItem(null);
          setCommentItem(it);
        }}
      />
      <ItemEditDialog
        target={editTarget}
        planId={plan.id}
        onClose={() => setEditTarget(null)}
      />
      <JsonItemsDialog
        open={jsonItemsOpen}
        planId={plan.id}
        items={plan.items}
        onClose={() => setJsonItemsOpen(false)}
      />
      <CommentDrawer item={commentItem} onClose={() => setCommentItem(null)} />
      <MembersDialog
        open={membersOpen}
        planId={plan.id}
        myRole={plan.role}
        onClose={() => setMembersOpen(false)}
      />
      <ConfirmDialog
        open={deletePlanOpen}
        onClose={() => setDeletePlanOpen(false)}
        onConfirm={() => deletePlanMutation.mutate()}
        title="이 계획 전체를 삭제하시겠어요?"
        message={`"${plan.title}" 계획과 모든 일정·코멘트가 사라집니다.`}
        confirmLabel="삭제"
        destructive
      />
    </div>
  );
}
